import React, { useMemo, useState, useEffect } from 'react';
import { chimneyService } from '../services/chimneyService';
import { appointmentService } from '../services/appointmentService';
import { messageService } from '../services/messageService';
import Layout from '../components/Layout';

const STATUS_LABELS = {
  pending: 'Oczekująca',
  confirmed: 'Potwierdzona',
  completed: 'Zakończona',
  cancelled: 'Anulowana',
};

const STATUS_CLASSES = {
  pending: 'text-yellow-700 bg-yellow-100',
  confirmed: 'text-green-700 bg-green-100',
  completed: 'text-blue-700 bg-blue-100',
  cancelled: 'text-red-700 bg-red-100',
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);

const ChimneyDashboard = () => {
  const [chimney, setChimney] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    city: '',
    description: '',
    phone: '',
  });
  const [saving, setSaving] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));
  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [chimneyData, appointmentsData, messagesData] = await Promise.all([
          chimneyService.getMyChimney(),
          appointmentService.getAll(),
          messageService.getRooms(),
        ]);
        setChimney(chimneyData);
        setFormData({
          company_name: chimneyData.company_name || '',
          city: chimneyData.city || '',
          description: chimneyData.description || '',
          phone: chimneyData.phone || '',
        });
        setAppointments(Array.isArray(appointmentsData) ? appointmentsData : appointmentsData.results || []);
        setMessages(Array.isArray(messagesData) ? messagesData : messagesData.results || []);
      } catch (err) {
        console.error('Błąd ładowania danych:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updated = await chimneyService.update(chimney.id, formData);
      setChimney(updated);
      setEditing(false);
      alert('Dane zostały zaktualizowane');
    } catch (err) {
      alert('Nie udało się zaktualizować danych');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const normalizeAppointments = useMemo(
    () =>
      [...appointments].sort(
        (a, b) => new Date(a.appointment_date).getTime() - new Date(b.appointment_date).getTime()
      ),
    [appointments]
  );

  const appointmentsByDay = useMemo(() => {
    const map = {};
    normalizeAppointments.forEach((appointment) => {
      const key = appointment.appointment_date.slice(0, 10);
      if (!map[key]) {
        map[key] = [];
      }
      map[key].push(appointment);
    });
    return map;
  }, [normalizeAppointments]);

  const selectedAppointments = appointmentsByDay[selectedDate] || [];

  const monthGrid = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const firstDayOfWeek = (monthStart.getDay() + 6) % 7;
    const cells = [];

    for (let i = 0; i < firstDayOfWeek; i += 1) {
      cells.push(null);
    }

    for (let day = 1; day <= monthEnd.getDate(); day += 1) {
      cells.push(new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day));
    }

    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    return cells;
  }, [currentMonth]);

  const updateAppointmentInState = (updatedAppointment) => {
    setAppointments((prev) =>
      prev.map((appointment) => (appointment.id === updatedAppointment.id ? updatedAppointment : appointment))
    );
  };

  const handleAppointmentAction = async (appointmentId, actionName) => {
    const handlers = {
      confirm: appointmentService.confirm,
      complete: appointmentService.complete,
      cancel: appointmentService.cancel,
    };

    const labels = {
      confirm: 'potwierdzić',
      complete: 'oznaczyć jako zakończoną',
      cancel: 'anulować',
    };

    const handler = handlers[actionName];
    if (!handler) return;

    if (!window.confirm(`Czy na pewno chcesz ${labels[actionName]} tę wizytę?`)) {
      return;
    }

    setActionLoadingId(appointmentId);
    try {
      const updated = await handler(appointmentId);
      updateAppointmentInState(updated);
    } catch (err) {
      alert(err.response?.data?.detail || 'Nie udało się zaktualizować wizyty');
    } finally {
      setActionLoadingId(null);
    }
  };

  const monthLabel = currentMonth.toLocaleDateString('pl-PL', {
    month: 'long',
    year: 'numeric',
  });

  if (loading) {
    return (
      <Layout>
        <div className="ik-container py-8">
          <div className="text-center text-ember-600 text-xl">Ładowanie...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="ik-container py-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-soot-950 mb-8">
            Panel kominiarza
          </h1>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Company Info */}
            <div className="rounded-md border border-soot-200 bg-white shadow-soft p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold text-soot-950">
                  Dane firmy
                </h2>
                {!editing && (
                  <button
                    onClick={() => setEditing(true)}
                    className="px-4 py-2 bg-ember-600 text-white rounded-lg hover:bg-ember-700 transition"
                  >
                    Edytuj
                  </button>
                )}
              </div>

              {editing ? (
                <form onSubmit={handleSave}>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nazwa firmy
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        value={formData.company_name}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-soot-300 rounded-md focus:outline-none focus:ring-ember-500 focus:border-ember-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Miasto
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-soot-300 rounded-md focus:outline-none focus:ring-ember-500 focus:border-ember-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Telefon
                      </label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-soot-300 rounded-md focus:outline-none focus:ring-ember-500 focus:border-ember-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Opis
                      </label>
                      <textarea
                        name="description"
                        rows="4"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-soot-300 rounded-md focus:outline-none focus:ring-ember-500 focus:border-ember-500"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="px-4 py-2 bg-ember-600 text-white rounded-lg hover:bg-ember-700 transition disabled:opacity-50"
                      >
                        {saving ? 'Zapisywanie...' : 'Zapisz'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(false);
                          setFormData({
                            company_name: chimney.company_name || '',
                            city: chimney.city || '',
                            description: chimney.description || '',
                            phone: chimney.phone || '',
                          });
                        }}
                        className="px-4 py-2 border-2 border-soot-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                      >
                        Anuluj
                      </button>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="space-y-2">
                  <p>
                    <span className="font-medium">Nazwa:</span>{' '}
                    {chimney.company_name || 'Brak'}
                  </p>
                  <p>
                    <span className="font-medium">Miasto:</span>{' '}
                    {chimney.city || 'Brak'}
                  </p>
                  <p>
                    <span className="font-medium">Telefon:</span>{' '}
                    {chimney.phone || 'Brak'}
                  </p>
                  <p>
                    <span className="font-medium">Opis:</span>{' '}
                    {chimney.description || 'Brak'}
                  </p>
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="rounded-md border border-soot-200 bg-white shadow-soft p-6">
              <h2 className="text-2xl font-semibold text-soot-950 mb-4">
                Statystyki
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-gray-600">Umówione wizyty</p>
                  <p className="text-3xl font-bold text-ember-600">
                    {appointments.filter((a) => a.status !== 'cancelled').length}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Wizyty oczekujące</p>
                  <p className="text-3xl font-bold text-yellow-600">
                    {appointments.filter((a) => a.status === 'pending').length}
                  </p>
                </div>
                <div>
                  <p className="text-gray-600">Wiadomości</p>
                  <p className="text-3xl font-bold text-ember-600">
                    {messages.length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar */}
          <div className="mt-6 rounded-md border border-soot-200 bg-white shadow-soft p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-semibold text-soot-950">Kalendarz wizyt</h2>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setCurrentMonth(
                      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
                    )
                  }
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  ←
                </button>
                <div className="min-w-[180px] text-center font-medium capitalize">{monthLabel}</div>
                <button
                  type="button"
                  onClick={() =>
                    setCurrentMonth(
                      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
                    )
                  }
                  className="px-3 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  →
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 gap-2 text-center text-sm font-medium text-gray-500 mb-2">
              {['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nd'].map((label) => (
                <div key={label}>{label}</div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-2">
              {monthGrid.map((cellDate, index) => {
                if (!cellDate) {
                  return <div key={`empty-${index}`} className="min-h-[100px] rounded-lg bg-gray-50" />;
                }

                const key = `${cellDate.getFullYear()}-${String(cellDate.getMonth() + 1).padStart(2, '0')}-${String(
                  cellDate.getDate()
                ).padStart(2, '0')}`;
                const items = appointmentsByDay[key] || [];
                const isSelected = key === selectedDate;

                return (
                  <button
                    type="button"
                    key={key}
                    onClick={() => setSelectedDate(key)}
                    className={`min-h-[100px] rounded-lg border p-2 text-left transition ${
                      isSelected ? 'border-ember-600 ring-2 ring-ember-100' : 'border-soot-200 hover:border-ember-600'
                    }`}
                  >
                    <div className="font-semibold">{cellDate.getDate()}</div>
                    <div className="mt-2 space-y-1">
                      {items.slice(0, 3).map((appointment) => (
                        <div
                          key={appointment.id}
                          className={`truncate rounded px-2 py-1 text-xs ${STATUS_CLASSES[appointment.status] || 'bg-gray-100'}`}
                        >
                          {new Date(appointment.appointment_date).toLocaleTimeString('pl-PL', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}{' '}
                          {appointment.user?.first_name || 'Klient'}
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div className="text-xs text-gray-500">+{items.length - 3} więcej</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Day appointments */}
          <div className="mt-6 rounded-md border border-soot-200 bg-white shadow-soft p-6">
            <h2 className="text-2xl font-semibold text-soot-950 mb-4">
              Wizyty na dzień {selectedDate}
            </h2>
            {selectedAppointments.length === 0 ? (
              <p className="text-gray-600">Brak wizyt w wybranym dniu.</p>
            ) : (
              <div className="space-y-4">
                {selectedAppointments.map((appointment) => (
                  <div key={appointment.id} className="border rounded-lg p-4">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div>
                        <p className="font-semibold text-lg">{formatDate(appointment.appointment_date)}</p>
                        <p className="text-gray-700">
                          {appointment.user?.first_name} {appointment.user?.last_name}
                        </p>
                        {appointment.user?.phone && (
                          <p className="text-sm text-gray-600">Telefon: {appointment.user.phone}</p>
                        )}
                        {appointment.user?.email && (
                          <p className="text-sm text-gray-600">Email: {appointment.user.email}</p>
                        )}
                        {appointment.notes && (
                          <p className="text-sm text-gray-600 mt-2">Notatki: {appointment.notes}</p>
                        )}
                        <span
                          className={`inline-block mt-3 rounded-full px-3 py-1 text-sm font-medium ${
                            STATUS_CLASSES[appointment.status] || 'text-gray-700 bg-gray-100'
                          }`}
                        >
                          {STATUS_LABELS[appointment.status] || appointment.status}
                        </span>
                      </div>
                      <div className="flex flex-col gap-2 min-w-[160px]">
                        {appointment.status === 'pending' && (
                          <button
                            type="button"
                            onClick={() => handleAppointmentAction(appointment.id, 'confirm')}
                            disabled={actionLoadingId === appointment.id}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                          >
                            Potwierdź
                          </button>
                        )}
                        {appointment.status !== 'completed' && appointment.status !== 'cancelled' && (
                          <button
                            type="button"
                            onClick={() => handleAppointmentAction(appointment.id, 'complete')}
                            disabled={actionLoadingId === appointment.id}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                          >
                            Zakończ
                          </button>
                        )}
                        {appointment.status !== 'cancelled' && (
                          <button
                            type="button"
                            onClick={() => handleAppointmentAction(appointment.id, 'cancel')}
                            disabled={actionLoadingId === appointment.id}
                            className="px-4 py-2 border-2 border-red-500 text-red-500 rounded-lg hover:bg-red-500 hover:text-white transition disabled:opacity-50"
                          >
                            Anuluj
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming appointments */}
          <div className="mt-6 rounded-md border border-soot-200 bg-white shadow-soft p-6">
            <h2 className="text-2xl font-semibold text-soot-950 mb-4">
              Najbliższe wizyty
            </h2>
            {normalizeAppointments.length === 0 ? (
              <p className="text-gray-600">Brak umówionych wizyt</p>
            ) : (
              <div className="space-y-3">
                {normalizeAppointments
                  .filter((a) => a.status !== 'cancelled')
                  .slice(0, 5)
                  .map((appointment) => (
                    <div key={appointment.id} className="border-b border-soot-200 pb-3">
                      <p className="font-medium">
                        {formatDate(appointment.appointment_date)}
                      </p>
                      <p className="text-gray-600">
                        {appointment.user?.first_name} {appointment.user?.last_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        Status: {STATUS_LABELS[appointment.status] || appointment.status}
                      </p>
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* Messages */}
          <div className="mt-6 rounded-md border border-soot-200 bg-white shadow-soft p-6">
            <h2 className="text-2xl font-semibold text-soot-950 mb-4">
              Ostatnie wiadomości
            </h2>
            {messages.length === 0 ? (
              <p className="text-gray-600">Brak wiadomości</p>
            ) : (
              <div className="space-y-3">
                {messages.slice(0, 5).map((room) => (
                  <a
                    key={room.id}
                    href={`/chat/${room.id}`}
                    className="block border-b border-soot-200 pb-3 hover:text-ember-600 transition"
                  >
                    <p className="font-medium">
                      {room.user?.first_name} {room.user?.last_name}
                    </p>
                    {room.last_message && (
                      <p className="text-gray-600 text-sm truncate">
                        {room.last_message.content}
                      </p>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ChimneyDashboard;
